<template>
    <Card :style="{ borderLeft: `4px solid ${getDepthColor(depth)}` }">
        <template #title>
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-1" v-if="elementType != 'filter'">
                    Condition:
                    <SelectButton v-model="group.condition" :options="['AND', 'OR']" :allow-empty="false" />
                </div>
                <div class="flex items-center gap-2">
                    <Button 
                        v-if="canConvertToSimple" 
                        icon="pi pi-arrow-down-left-and-arrow-up-right-to-center" 
                        rounded 
                        variant="text"
                        @click="convertToSimple"
                        v-tooltip="'Convert to simple expression'"
                    ></Button>
                    <Button v-if="allowDelete" icon="pi pi-trash" rounded raised @click="emitRemove"></Button>
                </div>
            </div>
        </template>

        <template #content >
            <div class="expression-body">
                <div
                    v-for="(expr, index) in expressions"
                    :key="expr.id"
                    class="expression-item"
                >
                    <template v-if="isGroup(expr)">
                        <!-- Nested group -->
                        <NestedExpressionBuilder
                            v-model="expressions[index]"
                            :depth="depth + 1"
                            @remove="removeExpr(index)"
                            @update="update"
                            @convertToSimple="convertNestedGroupToSimple(index)"
                            :elementType="elementType"
                        />
                    </template>
                    <template v-else>
                        <!-- Leaf expression -->
                        <div class="flex items-center justify-between gap-1 mr-4">
                            <ExpressionEditor
                                :elementType="elementType"
                                :element="expr.expression"
                                @delete="removeExpr(index)"
                                @update="update"
                                @transformToGroup="transformToGroup(index)"/>
                        </div>
                    </template>
                </div>

                <div class="expression-actions">
                    <Button rounded variant="text" icon="pi pi-plus" @click="addLeaf(false)" label="Add Expression"></Button>
                    <Button rounded variant="text" severity="secondary" icon="pi pi-plus" @click="addLeaf(true)" label="Add PQL Expression"></Button>
                    <Button rounded variant="text" v-if="elementType != 'filter'" severity="contrast" icon="pi pi-plus" @click="addGroup" label="Add Group"></Button>
                </div>
            </div>
        </template>
    </Card>
</template>

<script setup>
import {computed, markRaw, reactive, watch} from 'vue';
import NestedExpressionBuilder from './NestedExpressionBuilder.vue';
import ExpressionEditor from "./ExpressionEditor.vue";
import {getProperty} from "@/lib/util/ModelUtil";
import {useStore} from "vuex";

const store = useStore()

const props = defineProps({
    modelValue: {
        type: Object,
        required: true
    },
    allowDelete: {
        type: Boolean,
        default: true
    },
    depth: {
        type: Number,
        default: 0
    },
    elementType: {
        type: String
    },
    defaultTable: {
        type: String,
        default: ''
    }
});
const emit = defineEmits(['update:modelValue', 'remove', 'convertToSimple']);

// Local reactive copy to avoid mutating prop directly
const group = reactive(props.modelValue);

// Sync local changes back to parent
watch(
    () => group,
    (newVal) => emit('update:modelValue', newVal),
    {deep: true}
);

const update = () => {
    emit('update:modelValue', group);
}

const expressions = computed(() => getProperty(group, 'expressions') ? getProperty(group, 'expressions').filter(g => g.type) : [])

const canConvertToSimple = computed(() => {
    return expressions.value.length === 1 && !isGroup(expressions.value[0]) && props.allowDelete;
})

const isGroup = (obj) => obj && obj.type === 'group';

const modeler = computed(() => store.getters["graphStore/getModeler"])
const pacFactory = computed(() => {
    const currentModeler = modeler.value
    if (!currentModeler?.get) {
        return null
    }
    return markRaw(currentModeler.get('pacFactory'))
})

const createLocalExpression = (usePql = false) => {
    if (usePql) {
        return {
            id: `local-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            mode: 'pql',
            pql: '',
            label: '',
            enabled: false,
            valid: false
        }
    }
    return {
        id: `local-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        mode: 'static',
        label: '',
        enabled: false,
        valid: false,
        pql: '',
        scope: 'trace',
        reference_event: '',
        'event_attribute.table': props.defaultTable,
        'event_attribute.attribute': '',
        operator: '=',
        'compare_value.type': 'static',
        'compare_value.is_numeric': false,
        'compare_value.value': '',
        'compare_value.reference_event': '',
        'compare_value.event_attribute.table': '',
        'compare_value.event_attribute.attribute': ''
    }
}

const addLeaf = (usePql) => {
    if (pacFactory.value?.addCondition) {
        const ex = pacFactory.value.addCondition(group, usePql);
        store.commit('graphStore/setExpressionCurrentEditId', ex.id);
        return
    }

    if (!Array.isArray(group.expressions)) {
        group.expressions = []
    }
    const expression = createLocalExpression(usePql)
    group.expressions.push({
        id: expression.id,
        type: 'simple',
        expression
    })
    update()
}

const addGroup = () => {
    if (pacFactory.value?.addNestedGroup) {
        pacFactory.value.addNestedGroup(group);
        return
    }
    if (!Array.isArray(group.expressions)) {
        group.expressions = []
    }
    group.expressions.push({
        id: `local-group-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        type: 'group',
        condition: 'AND',
        expressions: []
    })
    update()
}

const removeExpr = (index) => {
    if (pacFactory.value?.deleteCondition) {
        pacFactory.value.deleteCondition(expressions.value[index], group);
        return
    }
    group.expressions.splice(index, 1)
    update()
}

const transformToGroup = (index) => {
    if (!pacFactory.value?.transformExpressionToGroup) {
        return
    }
    const simpleExpression = expressions.value[index];
    pacFactory.value.transformExpressionToGroup(simpleExpression, group);
    update();
}

const convertNestedGroupToSimple = (index) => {
    if (!pacFactory.value?.transformGroupToExpression) {
        return
    }
    const nestedGroup = expressions.value[index];
    pacFactory.value.transformGroupToExpression(nestedGroup, group);
    update();
}

const convertToSimple = () => {
    // This method should be called by the parent to transform this group to a simple expression
    emit('convertToSimple');
}

const emitRemove = () => {
    emit('remove');
}

// Function to get a color based on depth
const getDepthColor = (depth) => {
    const colors = ['#ffcccc', '#cce5ff', '#d5ffcc', '#fff5cc', '#e5ccff'];
    return colors[depth % colors.length];
}

</script>

<style scoped>

.expression-body {
    padding-left: 1em;
}

.expression-item {
    margin-bottom: 0.5em;
}

.expression-actions button {
    margin-right: 0.5em;
}
</style>